import { YOU_WERE_SUCCESSFULLY_LOGGED_IN_AS_username } from 'mk/autogenerated/translations/Login.sagas.4379fcb99fcaa71339ea0c0ea13ffbfa'
import { canManageBlogs, canManageForum, canManageGroups } from 'mk/bazaar/common/userUtils';
import { ACTIVE_SERVER_ID } from 'mk/settings';
import { users_api_auth_login_url, users_api_auth_login_via_otl_url } from 'mk/urls/functions';
import { login as loginActionCreator } from 'mk2/actions';
import { homepageUrl } from 'mk2/apps/home/urls';
import { LOGIN_FORM_NAME } from 'mk2/apps/users/components/LoginForm';
import {
    checkOneTimeLoginLinkAPI,
    loginAPI,
    CheckOneTimeLoginLinkTriggerAction,
    CHECK_ONETIME_LOGIN_LINK_TRIGGER,
    LoginNormalizedResponse,
    LoginTriggerAction,
    LOGIN_TRIGGER,
} from 'mk2/apps/users/containers/Login/Login.actions';
import { gotoUrlAfterLoginOrSignup, isNextPagePWA, storeLastKnownUsernameCookie } from 'mk2/apps/users/utils';
import { pageHeaderFetchFlaggedCountsTrigger, pageHeaderLoad } from 'mk2/containers/PageHeader/PageHeader.action';
import { normalizeError, XHRAction, XHRFormError } from 'mk2/helpers/api';
import { handleXHRFormErrorSaga, handleXHRFormSuccessSaga } from 'mk2/helpers/form';
import { getLogger } from 'mk2/logger';
import { getRequestBaseUrl, getRequestPermissions, AppStateProvidedFromServer } from 'mk2/reducers';
import { UserEntity, UserSchema } from 'mk2/schemas';
import { getRoutingLocation } from 'mk2/selectors';
import { interpolate } from 'mk2/services/i18n';
import { normalize } from 'normalizr';
import { startSubmit, touch } from 'redux-form';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

const logger = getLogger('users/Login.sagas');

interface ApiResponseLogin {
    body: {
        user: UserEntity;
        initialState: AppStateProvidedFromServer;
    };
}

function* checkOnetimeLoginLink({otlHash, xhr}: CheckOneTimeLoginLinkTriggerAction & XHRAction) {
    yield put(checkOneTimeLoginLinkAPI.request(otlHash));

    try {
        yield put(startSubmit(LOGIN_FORM_NAME));
        yield put(touch(LOGIN_FORM_NAME, 'username'));
        const response: ApiResponseLogin = yield call(
            () => xhr.post(users_api_auth_login_via_otl_url(), {
                otlHash,
            }),
        );

        // Login in redux before any URL redirect
        yield put(loginActionCreator(response.body.initialState));

        const normalizedResponse: LoginNormalizedResponse = normalize(response.body, {user: UserSchema});
        storeLastKnownUsernameCookie(response.body.user.username);

        const msg = interpolate(YOU_WERE_SUCCESSFULLY_LOGGED_IN_AS_username, {username: response.body.user.username});
        const baseUrl = yield select(getRequestBaseUrl);
        const location = yield select(getRoutingLocation);
        const canRedirectInPWA = isNextPagePWA(location, homepageUrl);
        const gotoUrl = gotoUrlAfterLoginOrSignup(baseUrl, location, homepageUrl, canRedirectInPWA);

        xhr.refreshApiAuth();
        if (canRedirectInPWA) {
            yield put(pageHeaderLoad());
            const requestUserPermissions: string[] = yield select(getRequestPermissions);
            const isBlogsAdmin = canManageBlogs(requestUserPermissions);
            const isForumAdmin = canManageForum(requestUserPermissions);
            const isGroupAdmin = canManageGroups(requestUserPermissions);
            if ((isBlogsAdmin || isForumAdmin || isGroupAdmin) && ACTIVE_SERVER_ID !== 207) {
                yield put(pageHeaderFetchFlaggedCountsTrigger());
            }
        }

        yield handleXHRFormSuccessSaga(LOGIN_FORM_NAME, gotoUrl, msg);
        yield put(checkOneTimeLoginLinkAPI.success(
            otlHash,
            response.body.user,
            response.body.initialState,
            normalizedResponse,
        ));
    } catch (error) {
        yield handleXHRFormErrorSaga(LOGIN_FORM_NAME, error as XHRFormError, logger);
        yield put(checkOneTimeLoginLinkAPI.failure(otlHash, normalizeError(error)));
    }
}

function* login({loginFd, xhr}: LoginTriggerAction & XHRAction) {
    yield put(loginAPI.request(loginFd));

    try {
        const response: ApiResponseLogin = yield call(
            () => xhr.post(users_api_auth_login_url(), {
                loginFd,
            }),
        );

        // Login in redux before any URL redirect
        yield put(loginActionCreator(response.body.initialState));

        const normalizedResponse: LoginNormalizedResponse = normalize(response.body, {user: UserSchema});
        storeLastKnownUsernameCookie(response.body.user.username);

        const msg = interpolate(YOU_WERE_SUCCESSFULLY_LOGGED_IN_AS_username, {username: response.body.user.username});
        const baseUrl = yield select(getRequestBaseUrl);
        const location = yield select(getRoutingLocation);
        const canRedirectInPWA = isNextPagePWA(location, homepageUrl);
        const gotoUrl = gotoUrlAfterLoginOrSignup(baseUrl, location, homepageUrl, canRedirectInPWA);

        xhr.refreshApiAuth();
        if (canRedirectInPWA) {
            yield put(pageHeaderLoad());
            const requestUserPermissions: string[] = yield select(getRequestPermissions);
            const isBlogsAdmin = canManageBlogs(requestUserPermissions);
            const isForumAdmin = canManageForum(requestUserPermissions);
            const isGroupAdmin = canManageGroups(requestUserPermissions);
            if ((isBlogsAdmin || isForumAdmin || isGroupAdmin) && ACTIVE_SERVER_ID !== 207) {
                yield put(pageHeaderFetchFlaggedCountsTrigger());
            }
        }

        yield handleXHRFormSuccessSaga(LOGIN_FORM_NAME, gotoUrl, msg);
        yield put(loginAPI.success(
            loginFd,
            response.body.user,
            response.body.initialState,
            normalizedResponse,
        ));
    } catch (error) {
        yield handleXHRFormErrorSaga(LOGIN_FORM_NAME, error as XHRFormError, logger);
        yield put(loginAPI.failure(loginFd, normalizeError(error)));
    }
}

export default function* root() {
    yield all([
        takeLatest(LOGIN_TRIGGER, login),
        takeLatest(CHECK_ONETIME_LOGIN_LINK_TRIGGER, checkOnetimeLoginLink),
    ]);
}
